import React, { useEffect, useState } from "react";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import MediaQuery from "components/MediaQuery";
import { usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";
import { ReactComponent as FilterIconNew } from "svg/filter-icon-new.svg";
import ItemTemp from "@outplayed/tooltips/src/tooltipImages/ItemTemp";
import { ReactComponent as SearchIcon } from "svg/search-icon.svg";

const itemClass = "items-pic-small items-border-normal-small";

const keywords = [
  "start",
  "starter",
  "starters",
  "consum",
  "consumable",
  "consumables",
  "trinket",
  "trinkets",
  "dist",
  "dists",
  "distributed",
  "boot",
  "boots",
  "basic",
  "epic",
  "legend",
  "legendary",
  "mythic",
  "orn",
  "ornn",
  "ad",
  "attack",
  "attack damage",
  "ap",
  "ability",
  "ability power",
  "as",
  "attack speed",
  "ms",
  "move speed",
  "movement speed",
  "speed",
  "cd",
  "cdr",
  "haste",
  "ability haste",
  "crit",
  "critical",
  "critical hit",
  "critical strike",
  "critical strike chance",
  "armor pen",
  "armor penetration",
  "pen",
  "magic pen",
  "magic penetration",
  "pen",
  "leth",
  "lethality",
  "omni",
  "omnivamp",
  "hp",
  "health",
  "mana",
  "armor",
  "armour",
  "mr",
  "magic resist",
  "bhr",
  "health regen",
  "base health regen",
  "bmr",
  "mana regen",
  "base mana regen",
  "heal",
  "heals",
  "shield",
  "shields",
  "heal and shield",
  "heal and shield power",
  "gp10",
  "gp5",
  "gold",
  "goldper10",
  "gold per 10 seconds",
  "sup",
  "supp",
  "support",
  "tank",
];

const filters = {
  "Starter Items": ["start", "starter", "starters"],
  "Consumable Items": ["consum", "consumable", "consumables"],
  Trinkets: ["trinket", "trinkets"],
  "Distributed Items": ["dist", "dists", "distributed"],
  Boots: ["boot", "boots"],
  "Basic Items": ["basic"],
  "Epic Items": ["epic"],
  "Legendary Items": ["legend", "legendary"],
  "Mythic Items": ["mythic"],
  "Ornn Items": ["orn", "ornn"],
  "Attack Damage": ["ad", "attack", "attack damage"],
  "Ability Power": ["ap", "ability", "ability power"],
  "Attack Speed": ["as", "attack speed"],
  "Move Speed": ["ms", "move speed", "movement speed", "speed"],
  "Ability Haste": ["cd", "cdr", "haste", "ability haste"],
  "Critical Strike Chance": ["crit", "critical", "critical hit", "critical strike", "critical strike chance"],
  "Armor Penetration": ["armor pen", "armor penetration", "pen"],
  "Magic Penetration": ["magic pen", "magic penetration", "pen"],
  Lethality: ["leth", "lethality"],
  "Life Steal": ["life", "steal", "life steal"],
  Omnivamp: ["omni", "omnivamp"],
  Health: ["hp", "health"],
  Mana: ["mana"],
  Armor: ["armor", "armour", "tank"],
  "Magic Resist": ["mr", "magic resist", "tank"],
  "Base Health Regen": ["bhr", "health regen", "base health regen"],
  "Base Mana Regen": ["bmr", "mana regen", "base mana regen"],
  "Heal and Shield Power": ["heal", "heals", "shield", "shields", "heal and shield", "heal and shield power"],
  "Gold Per 10 Seconds": ["gp10", "gp5", "gold", "goldper10", "gold per 10 seconds", "sup", "supp", "support"],
};

const ItemsDirectory = (props) => {
  const { getItems, useNewRiotItems, useItemsWiki } = getRiotAssetsContext();

  const getPageTitleAndDesc = usePageTitleAndDesc();
  const itemsList = useItemsWiki({ ssr: true });
  const { data: items, loading: fetchingItems, error: errorItems } = useNewRiotItems({ ssr: true });
  const itemsJSON = items;
  const [input, setInput] = useState("");
  const [activeFilterTypes, setActiveFilterTypes] = useState([]);

  const [activeItemTypes, setActiveItemTypes] = useState({
    "Starter Items": true,
    "Consumable Items": true,
    Trinkets: true,
    "Distributed Items": true,
    Boots: true,
    "Basic Items": true,
    "Epic Items": true,
    "Legendary Items": true,
    "Mythic Items": false,
    "Ornn Items": true,
  });

  // setting activeFilterTypes
  useEffect(() => {
    const inputLower = input.toLowerCase();
    // when user types a specific keyword, find the associated filter type
    if (itemsJSON && keywords.includes(inputLower)) {
      const filterKeys = Object.keys(filters);
      filterKeys.forEach((key) => {
        if (filters[key].includes(inputLower)) {
          setActiveFilterTypes((prev) => [...prev, key]);
        }
      });
    } else {
      setActiveFilterTypes([]);
    }
  }, [input]);

  // setting activeItemTypes
  useEffect(() => {
    if (itemsJSON) {
      const types = Object.keys(activeItemTypes);
      const inputLower = input.toLowerCase();
      types.forEach((type) => {
        const index = itemTypes.findIndex((x) => x.name === type);
        const items = itemTypes[index].items;
        let filteredItems;
        if (keywords.includes(inputLower) && filters[type].includes(inputLower)) {
          filteredItems = items;
        } else if (keywords.includes(inputLower)) {
          // i.e ad, ap, lethality cdr, haste
          const filterKeys = Object.keys(filters);
          filterKeys.forEach((key) => {
            if (filters[key].includes(inputLower)) {
              filteredItems = items.filter((itemId) => itemsJSON[itemId].description.toLowerCase().includes(key.toLowerCase()));
            }
          });
        } else {
          filteredItems =
            !itemsList.loading && items
              ? items.filter((itemId) => itemsJSON[itemId].name.toLowerCase().includes(inputLower))
              : [];
        }
        setActiveItemTypes((prev) => ({
          ...prev,
          [type]: filteredItems.length > 0 ? true : false,
        }));
      });
    }
  }, [input]);

  // for each item type (ie basic, epic, mythic)
  // first check: if the type is active (activeItemTypes), then show the container
  // then for each itemId under the specific item type,
  // first check: if the respective item type is inside activeFilterTypes, show item
  // second check: if the input is a keyword, if the item description contains a filter keyword, show item
  // third check: if the input is a substring of the item name, show item
  // else don't show item

  const itemTypes = [
    {
      name: "Starter Items",
      items: itemsList?.data?.starterItems,
    },
    {
      name: "Consumable Items",
      items: itemsList?.data?.consumableItems,
    },
    {
      name: "Trinkets",
      items: itemsList?.data?.trinketItems,
    },
    {
      name: "Distributed Items",
      items: itemsList?.data?.distributedItems,
    },
    {
      name: "Boots",
      items: itemsList?.data?.bootItems,
    },
    {
      name: "Basic Items",
      items: itemsList?.data?.basicItems,
    },
    {
      name: "Epic Items",
      items: itemsList?.data?.epicItems,
    },
    {
      name: "Legendary Items",
      items: itemsList?.data?.legendaryItems,
    },
    {
      name: "Mythic Items",
      items: itemsList?.data?.mythicItems,
    },
    {
      name: "Ornn Items",
      items: itemsList?.data?.ornnItems,
    },
  ];

  if (itemsList.loading) {
    return (
      <div className="spinthatshit-loader" style={{ height: "80vh" }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="items-directory-home-page">
      <div className="items-directory-home content-side-padding">
        {getPageTitleAndDesc("items")}
        <div className="directory-header">
          <h1>League of Legends Items</h1>
          <h2>
            All in-game items for League of Legends, including mythics, legendary, starting items, and consumables. Up-to-date
            stats, passives, actives, and gold costs
          </h2>
        </div>
        <div className="filter-manager filter-manager-items">
          <div className="filter-manager_container">
            <div className="filter-manager_label">
              <FilterIconNew />
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <span>Filters</span>
              </MediaQuery>
            </div>
            <ItemsSearchBar input={input} setInput={setInput} />
          </div>
        </div>
        <div className="directory-items-container">
          {itemTypes.map(
            (type) =>
              activeItemTypes[type.name] && (
                <div className="directory-items-section" key={type.name}>
                  <div className="items-header">{type.name}</div>
                  <div className="items-container">
                    {type.items.map((id) =>
                      activeFilterTypes.includes(type.name) ? (
                        <ItemTemp key={id} itemId={id} className={itemClass} ssr={true} enableLink={true} />
                      ) : keywords.includes(input.toLowerCase()) ? (
                        itemsJSON &&
                        activeFilterTypes.some((string) => itemsJSON[id].description.includes(string)) && (
                          <ItemTemp key={id} itemId={id} className={itemClass} ssr={true} enableLink={true} />
                        )
                      ) : (
                        itemsJSON &&
                        itemsJSON[id].name.toLowerCase().includes(input.toLowerCase()) && (
                          <ItemTemp key={id} itemId={id} className={itemClass} ssr={true} enableLink={true} />
                        )
                      ),
                    )}
                  </div>
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemsDirectory;

const ItemsSearchBar = (props) => {
  const { input, setInput } = props;

  const handleOnChange = (e) => {
    setInput(e.target.value);
  };

  return (
    <div className="items-search-input">
      <input placeholder="Search Item" type="text" value={input} onChange={handleOnChange} />
      <SearchIcon />
    </div>
  );
};
